import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Calendar, Users, Truck, Settings, SendHorizonal, Save, Loader, AlertTriangle, X } from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', bord2: 'var(--nd-w10)',
  text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', green: '#34D399', red: '#F87171', amber: '#FBBF24',
  font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes modalIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }

  .ejom-input, .ejom-select, .ejom-date {
    width:100%; background:var(--nd-w05); border:1px solid var(--nd-w10);
    border-radius:9px; color:var(--nd-text); font-size:13px; font-family:'DM Sans',sans-serif;
    padding:9px 12px 9px 34px; height:40px; outline:none;
    transition:border-color 0.15s,background 0.15s;
  }
  .ejom-date  { color-scheme:dark; }
  .ejom-select { appearance:none; cursor:pointer; color-scheme:dark; }
  .ejom-select option { background:var(--nd-surface-2); color:var(--nd-text); }
  .ejom-input:focus,.ejom-select:focus,.ejom-date:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }
  .ejom-label { display:block; font-size:10px; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--nd-tx40); margin-bottom:5px; }

  /* react-select dark styles */
  .ejom-rs .react-select__control { background:var(--nd-w05)!important; border:1px solid var(--nd-w10)!important; border-radius:9px!important; min-height:40px!important; box-shadow:none!important; }
  .ejom-rs .react-select__control--is-focused { border-color:rgba(59,130,246,0.55)!important; background:rgba(59,130,246,0.05)!important; }
  .ejom-rs .react-select__menu { background:var(--nd-surface-2)!important; border:1px solid var(--nd-w10)!important; border-radius:9px!important; }
  .ejom-rs .react-select__option { background:transparent!important; color:var(--nd-text)!important; font-size:12px; }
  .ejom-rs .react-select__option--is-focused { background:rgba(59,130,246,0.12)!important; }
  .ejom-rs .react-select__option--is-selected { background:rgba(59,130,246,0.22)!important; }
  .ejom-rs .react-select__multi-value { background:rgba(59,130,246,0.18)!important; border-radius:5px!important; }
  .ejom-rs .react-select__multi-value__label { color:#60A5FA!important; font-size:11px!important; }
  .ejom-rs .react-select__multi-value__remove { color:#60A5FA!important; }
  .ejom-rs .react-select__multi-value__remove:hover { background:rgba(248,113,113,0.18)!important; color:#F87171!important; }
  .ejom-rs .react-select__placeholder,.ejom-rs .react-select__input-container { color:var(--nd-tx22)!important; font-size:12px!important; }
  .ejom-rs .react-select__single-value { color:var(--nd-text)!important; font-size:12px!important; }
  .ejom-rs .react-select__indicators { opacity:0.4; }

  /* dispatch button */
  .ejom-btn-dispatch { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border-radius:9px; border:none; font-family:'DM Sans',sans-serif; font-size:12px; font-weight:700; cursor:pointer; transition:all 0.15s; }
  .ejom-btn-dispatch.ready    { background:linear-gradient(135deg,#22D3A5,#3B82F6); color:var(--nd-text-strong); box-shadow:0 4px 12px rgba(34,211,165,0.25); }
  .ejom-btn-dispatch.ready:hover { opacity:.88; }
  .ejom-btn-dispatch.muted    { background:var(--nd-w06); color:var(--nd-tx30); cursor:not-allowed; border:1px solid var(--nd-w08); }

  .ejom-btn-save { display:inline-flex; align-items:center; gap:7px; padding:9px 20px; border-radius:9px; border:none; background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; cursor:pointer; box-shadow:0 4px 14px rgba(59,130,246,0.25); transition:opacity 0.15s; }
  .ejom-btn-save:hover { opacity:.88; }
  .ejom-btn-cancel { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; border-radius:9px; border:1px solid var(--nd-w10); background:var(--nd-w05); color:var(--nd-tx45); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .ejom-btn-cancel:hover { background:var(--nd-w08); color:var(--nd-tx70); }
`;

const Field = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label className="ejom-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
const EditJobOrderModal = ({ show, onHide, jobOrder, onJobUpdated }) => {
  const [scheduledDate, setScheduledDate] = useState('');
  const [team, setTeam]                   = useState([]);
  const [teamsList, setTeamsList]         = useState([]);
  const [loading, setLoading]             = useState(false);
  const [dispatching, setDispatching]     = useState(false);
  const [dispatchError, setDispatchError] = useState('');
  const [vehicleId, setVehicleId]         = useState('');
  const [machineryId, setMachineryId]     = useState('');
  const [vehicles, setVehicles]           = useState([]);
  const [machineries, setMachineries]     = useState([]);

  useEffect(() => {
    if (jobOrder) {
      setScheduledDate(new Date(jobOrder.scheduled_date).toLocaleDateString('en-CA'));
      setTeam(
        jobOrder.assigned_team
          ? jobOrder.assigned_team.split(',').map(name => ({ label: name.trim(), value: name.trim() }))
          : []
      );
    }
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/team-members`)
      .then(res => setTeamsList(res.data.map(m => ({ label: m.name || m, value: m.name || m }))))
      .catch(() => setTeamsList([]));
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/vehicles`).then(res => setVehicles(res.data));
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/machinery`).then(res => setMachineries(res.data));
  }, [jobOrder]);

  const today          = new Date().toISOString().slice(0, 10);
  const isScheduledToday = scheduledDate === today;
  const isDispatched   = jobOrder?.status?.toLowerCase() === 'dispatched';
  const isCompleted    = jobOrder?.status?.toLowerCase() === 'completed';

  const handleDispatch = async () => {
    setDispatching(true); setDispatchError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/${jobOrder.job_id}/dispatch`, { dispatchedBy: 'Operations' });
      onJobUpdated(); onHide();
    } catch (err) { console.error(err); setDispatchError('Failed to dispatch job order.'); }
    finally { setDispatching(false); }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (scheduledDate !== jobOrder.scheduled_date?.slice(0, 10))
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/${jobOrder.job_id}/update-date`, { newDate: scheduledDate });
      if (JSON.stringify(team.map(t=>t.value)) !== JSON.stringify((jobOrder.assigned_team||'').split(',').map(t=>t.trim())))
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/${jobOrder.job_id}/assign-team`, { teamMembers: team.map(t=>t.value) });
      if (vehicleId) await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/${jobOrder.job_id}/assign-vehicle`, { vehicleId });
      if (machineryId) await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/${jobOrder.job_id}/assign-machinery`, { machineryId });
      setLoading(false); onJobUpdated(); onHide();
    } catch (err) { console.error(err); setLoading(false); alert('Failed to update job order.'); }
  };

  const dispatchDisabled = isDispatched || team.length === 0 || dispatching || isCompleted;
  const dispatchLabel = isDispatched ? 'Already Dispatched'
    : dispatching ? 'Dispatching…' : team.length === 0 ? 'Assign Team First' : 'Dispatch Job';

  if (!show) return null;

  return (
    <>
      <style>{STYLES}</style>
      <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',backdropFilter:'blur(4px)',zIndex:1050,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }} onClick={onHide}>
        <div style={{ background:T.surface,border:`1px solid ${T.bord2}`,borderRadius:16,width:'100%',maxWidth:480,maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 60px rgba(0,0,0,0.7)',animation:'modalIn 0.2s ease',fontFamily:T.font }} onClick={e=>e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding:'16px 20px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0 }}>
            <div style={{ display:'flex',alignItems:'center',gap:9 }}>
              <div style={{ width:28,height:28,borderRadius:8,background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <Settings size={14} color={T.blue} />
              </div>
              <div>
                <div style={{ fontSize:14,fontWeight:700,color:T.text }}>Edit Job Order</div>
                <div style={{ fontSize:10,color:T.muted,fontFamily:T.mono }}>#{jobOrder?.job_id}</div>
              </div>
            </div>
            <button onClick={onHide} style={{ background:'var(--nd-w06)',border:'1px solid var(--nd-w10)',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:T.muted }}>
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding:'16px 20px',overflowY:'auto',flex:1 }}>
            {loading ? (
              <div style={{ display:'flex',alignItems:'center',justifyContent:'center',padding:'32px',gap:12 }}>
                <Loader size={20} color={T.blue} style={{ animation:'spin 0.8s linear infinite' }} />
                <span style={{ color:T.muted,fontSize:13 }}>Saving changes…</span>
              </div>
            ) : (
              <>
                {dispatchError && (
                  <div style={{ background:'rgba(248,113,113,0.10)',border:'1px solid rgba(248,113,113,0.28)',borderRadius:9,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:8,color:T.red,fontSize:12 }}>
                    <AlertTriangle size={13} /> {dispatchError}
                  </div>
                )}

                <Field label="Scheduled Date" icon={Calendar} iconColor={T.blue}>
                  <input className="ejom-date" type="date" value={scheduledDate} onChange={e=>setScheduledDate(e.target.value)} />
                </Field>

                <div style={{ marginBottom: 14 }}>
                  <label className="ejom-label">Assign Team</label>
                  <div className="ejom-rs">
                    <Select
                      isMulti
                      options={teamsList}
                      value={team}
                      onChange={selectedOptions => setTeam(selectedOptions)}
                      placeholder="Select team members..."
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>

                <Field label="Assign Vehicle" icon={Truck} iconColor={T.teal}>
                  <select className="ejom-select" value={vehicleId} onChange={e=>setVehicleId(e.target.value)}>
                    <option value="">— Select Vehicle —</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
                  </select>
                </Field>

                <Field label="Assign Machinery" icon={Settings} iconColor={T.amber}>
                  <select className="ejom-select" value={machineryId} onChange={e=>setMachineryId(e.target.value)}>
                    <option value="">— Select Machinery —</option>
                    {machineries.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </Field>

                {isScheduledToday && (
                  <div style={{ paddingTop: 6 }}>
                    <button
                      className={`ejom-btn-dispatch ${dispatchDisabled ? 'muted' : 'ready'}`}
                      onClick={handleDispatch}
                      disabled={dispatchDisabled}
                    >
                      {dispatching
                        ? <><Loader size={12} style={{ animation:'spin 0.8s linear infinite' }} /> Dispatching…</>
                        : <><SendHorizonal size={12} /> {dispatchLabel}</>}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding:'12px 20px 16px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:8,flexShrink:0 }}>
            <button className="ejom-btn-cancel" onClick={onHide}>Close</button>
            <button className="ejom-btn-save" onClick={handleSave}><Save size={13} /> Save Changes</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditJobOrderModal;